import React, { useEffect, useState } from 'react'
import { ChevronUp, ChevronDown, Trash, Copy, Edit } from 'lucide-react'
import {
  Section,
  SectionTitle,
  GeneralList,
  SectionHeader,
  Button,
  SectionContent,
  Text,
  TextLight,
  SectionCardItem,
  CardTitle,
  CardHeader,
  ButtonIcons,
  IconButton,
  ObsDiv,
} from '../style'
import axios from '../../../../config/axios'
import { useSearchParams } from 'react-router-dom'
import { stageDisplayNumber, MAX_CHILDREN_PER_PARENT } from '../../../../pages/ClientPanel/ProductionFlow/stageNumbering'

interface StageRef {
  name: string
  id: number
}

interface SavedStageData {
  id: number
  serviceId: number
  stageId: number
  completed: boolean
  index: number
  parentId?: number | null
  description?: string
  observation?: string
  stage: StageRef
  Children?: SavedStageData[]
}

interface ShowServicesProps {
  setModalActive: React.Dispatch<React.SetStateAction<number | null>>
  refresh: boolean
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>
  setServiceToEdit: React.Dispatch<React.SetStateAction<any>>
}

const ShowServices = ({
  setModalActive,
  refresh,
  setRefresh,
  setServiceToEdit,
}: ShowServicesProps) => {
  const [rootStages, setRootStages] = useState<SavedStageData[]>([])
  const [searchParams] = useSearchParams()
  const opId = searchParams.get('id')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const fetchServiceStages = async () => {
      if (!opId) return
      setIsLoading(true)
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/services-of-op/${opId}`,
        )
        const raw = response.data || []
        const normalized = raw.map((root: SavedStageData) => ({
          ...root,
          stage: root.stage ?? {
            id: (root as { Stage?: StageRef }).Stage?.id ?? 0,
            name: (root as { Stage?: StageRef }).Stage?.name ?? '',
          },
          Children: (root.Children ?? []).map((child) => ({
            ...child,
            stage: child.stage ?? {
              id: (child as { Stage?: StageRef }).Stage?.id ?? 0,
              name: (child as { Stage?: StageRef }).Stage?.name ?? '',
            },
          })),
        }))
        setRootStages(normalized)
      } catch (error) {
        console.error('Erro ao buscar stages salvos:', error)
        setRootStages([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchServiceStages()
  }, [opId, refresh])

  const openEdit = (stage: SavedStageData, parentStageId?: number) => {
    setServiceToEdit({
      id: stage.id,
      stageId: stage.stageId,
      name: stage.stage.name,
      description: stage.description || '',
      observation: stage.observation || '',
      parentId: stage.parentId ?? parentStageId ?? null,
      parentStageId: parentStageId ?? null,
      image: null,
    })
    setModalActive(1)
  }

  const openAddChild = (parent: SavedStageData) => {
    const childCount = parent.Children?.length ?? 0
    if (childCount >= MAX_CHILDREN_PER_PARENT) return
    setServiceToEdit({
      parentStageId: parent.id,
      parentId: parent.id,
      image: null,
    })
    setModalActive(1)
  }

  const handleCopyService = async (stageId: number) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/service/duplicate-stage/${opId}`,
        { stageId },
      )
      if (response.data) setRefresh(!refresh)
    } catch (error) {
      console.log(error)
    }
  }

  const handleDelete = async (stageId: number) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/delete-stage/${stageId}`,
      )
      if (response.data) setRefresh(!refresh)
    } catch (error) {
      console.log(error)
    }
  }

  const moveSiblings = async (
    siblings: SavedStageData[],
    currentIndex: number,
    direction: 'up' | 'down',
    scope: 'root' | 'child',
    parentStageId?: number,
  ) => {
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
    if (newIndex < 0 || newIndex >= siblings.length) return
    const reordered = [...siblings]
    const [item] = reordered.splice(currentIndex, 1)
    reordered.splice(newIndex, 0, item)
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/production-flow/pcp/move-stage`,
        {
          scope,
          parentStageId: scope === 'child' ? parentStageId : undefined,
          servicesStages: reordered.map((stage, index) => ({ id: stage.id, index })),
        },
      )
      if (response.data) setRefresh(!refresh)
    } catch (error) {
      console.log(error)
    }
  }

  const renderStageCard = (
    stage: SavedStageData,
    parentIndex: number,
    childIndex?: number,
    siblings?: SavedStageData[],
    scope: 'root' | 'child' = 'root',
    parentStageId?: number,
    isChild = false,
  ) => {
    const list = siblings ?? rootStages
    const idx = list.findIndex((s) => s.id === stage.id)
    const label = stageDisplayNumber(parentIndex, childIndex)
    const childCount = stage.Children?.length ?? 0

    return (
      <SectionCardItem
        key={stage.id}
        style={isChild ? { marginLeft: '1.5rem', marginTop: '0.5rem' } : undefined}
      >
        <CardHeader>
          <CardTitle>
            {stage.stage.name} {label}
          </CardTitle>
          <ButtonIcons>
            <IconButton onClick={() => handleDelete(stage.id)} colors="rgba(220, 38, 38, ">
              <Trash size={16} />
            </IconButton>
            <IconButton onClick={() => handleCopyService(stage.id)} colors="rgba(220, 38, 38, ">
              <Copy size={16} />
            </IconButton>
            <IconButton onClick={() => openEdit(stage, parentStageId)} colors="rgba(220, 38, 38, ">
              <Edit size={16} />
            </IconButton>
            <IconButton
              className={idx === 0 ? 'disabled' : ''}
              onClick={() => moveSiblings(list, idx, 'up', scope, parentStageId)}
              colors="rgba(202, 138, 4, "
            >
              <ChevronUp size={16} />
            </IconButton>
            <IconButton
              className={idx === list.length - 1 ? 'disabled' : ''}
              onClick={() => moveSiblings(list, idx, 'down', scope, parentStageId)}
              colors="rgba(202, 138, 4, "
            >
              <ChevronDown size={16} />
            </IconButton>
          </ButtonIcons>
        </CardHeader>
        <p>{stage.description}</p>
        <ObsDiv>
          <p className="title">Observações:</p>
          <p>{stage.observation}</p>
        </ObsDiv>
        {!isChild && childCount < MAX_CHILDREN_PER_PARENT && (
          <Button
            style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}
            onClick={() => openAddChild(stage)}
          >
            Adicionar subetapa
          </Button>
        )}
      </SectionCardItem>
    )
  }

  return (
    <Section>
      <SectionHeader>
        <SectionTitle>Serviços</SectionTitle>
        <Button
          onClick={() => {
            setServiceToEdit(null)
            setModalActive(1)
          }}
        >
          Adicionar Serviço
        </Button>
      </SectionHeader>
      {rootStages.length > 0 ? (
        <SectionContent style={{ marginTop: '1rem' }} isEmpty={false} isLoading={isLoading}>
          <GeneralList>
            {rootStages.map((root, parentIndex) => (
              <React.Fragment key={root.id}>
                {renderStageCard(root, parentIndex, undefined, rootStages, 'root')}
                {(root.Children ?? [])
                  .slice()
                  .sort((a, b) => a.index - b.index)
                  .map((child, childIndex) =>
                    renderStageCard(
                      child,
                      parentIndex,
                      childIndex,
                      root.Children,
                      'child',
                      root.id,
                      true,
                    ),
                  )}
              </React.Fragment>
            ))}
          </GeneralList>
        </SectionContent>
      ) : (
        <SectionContent isEmpty={true} isLoading={isLoading}>
          <Text>Nenhum serviço programado</Text>
          <TextLight>{'Clique em "Adicionar Serviço" para começar'}</TextLight>
        </SectionContent>
      )}
    </Section>
  )
}

export default ShowServices
